import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI, seoAPI, geoAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  MagnifyingGlass, ArrowUp, ArrowDown, LinkBreak, Lightning, ChartLine, Shield, Gauge,
  Target, CheckCircle, X, Warning, Crosshair
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line
} from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';
import { FabricatedDataNotice } from '../components/brand/FabricatedDataNotice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });

export default function SEOPage() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [audits, setAudits] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [brokenLinks, setBrokenLinks] = useState([]);
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mustWin, setMustWin] = useState(null);
  const [answerFirst, setAnswerFirst] = useState(null);
  const [vernGap, setVernGap] = useState(null);

  useEffect(() => {
    clientsAPI.list().then(r => {
      setClients(r.data);
      if (r.data.length > 0) setSelectedClient(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedClient) return;
    setLoading(true);
    Promise.all([
      seoAPI.audits(selectedClient),
      seoAPI.keywords(selectedClient),
      seoAPI.brokenLinks(selectedClient),
      geoAPI.optimization(selectedClient),
      axios.get(`${API}/geo/must-win/${selectedClient}`, auth()).catch(() => ({ data: null })),
      axios.get(`${API}/geo/answer-first/${selectedClient}`, auth()).catch(() => ({ data: null })),
      axios.get(`${API}/geo/vernacular-gap/${selectedClient}`, auth()).catch(() => ({ data: null })),
    ]).then(([a, k, b, g, mw, af, vg]) => {
      setAudits(a.data);
      setKeywords(k.data);
      setBrokenLinks(b.data);
      setGeoData(g.data);
      setMustWin(mw.data);
      setAnswerFirst(af.data);
      setVernGap(vg.data);
    }).catch(() => toast.error('Failed to load SEO data'))
      .finally(() => setLoading(false));
  }, [selectedClient]);

  const handleRunAudit = async () => {
    try {
      await seoAPI.runAudit(selectedClient);
      toast.success('New audit started');
      const res = await seoAPI.audits(selectedClient);
      setAudits(res.data);
    } catch { toast.error('Failed to run audit'); }
  };

  const latestAudit = audits[0];
  const radarData = latestAudit ? [
    { subject: 'Performance', A: latestAudit.performance_score },
    { subject: 'Accessibility', A: latestAudit.accessibility_score },
    { subject: 'SEO', A: latestAudit.seo_score },
    { subject: 'Best Practices', A: latestAudit.best_practices },
  ] : [];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="seo-page">
        <FabricatedDataNotice reason="SEO audit data below is seeded on first load and then persisted, not sourced from a real crawl or search-console connection. GEO Report data in this app is real; this SEO Audit view is not." />
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight">
              SEO & GEO
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Technical audits, keyword tracking & generative engine optimization</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger data-testid="seo-client-select" className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">{c.display_name || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="run-audit-btn" onClick={handleRunAudit} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
              <Lightning size={16} className="mr-2" /> Run Audit
            </Button>
          </div>
        </div>

        {!selectedClient ? (
          <div className="nx-metric-card rounded-lg text-center py-12">
            <MagnifyingGlass size={40} className="mx-auto mb-3 text-[var(--text-tertiary)]" />
            <p className="text-[var(--text-secondary)]">Select a client to view SEO data</p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" />
          </div>
        ) : (
          <Tabs defaultValue="audits" className="space-y-4">
            <TabsList className="bg-[var(--surface)] border border-[var(--border)]">
              <TabsTrigger value="audits" data-testid="tab-audits" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Audits</TabsTrigger>
              <TabsTrigger value="keywords" data-testid="tab-keywords" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Keywords</TabsTrigger>
              <TabsTrigger value="broken-links" data-testid="tab-broken-links" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Broken Links</TabsTrigger>
              <TabsTrigger value="geo" data-testid="tab-geo" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">GEO</TabsTrigger>
              <TabsTrigger value="must-win" data-testid="tab-must-win" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
                <Crosshair size={12} className="mr-1" /> Must-Win
              </TabsTrigger>
              <TabsTrigger value="answer-first" data-testid="tab-answer-first" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">40-Word Rule</TabsTrigger>
              <TabsTrigger value="vern-gap" data-testid="tab-vern-gap" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Vernacular Gap</TabsTrigger>
            </TabsList>

            {/* Audits Tab */}
            <TabsContent value="audits" className="space-y-4">
              {latestAudit && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Performance', value: latestAudit.performance_score, icon: Gauge, color: 'var(--accent)' },
                    { label: 'Accessibility', value: latestAudit.accessibility_score, icon: Shield, color: 'var(--success)' },
                    { label: 'SEO Score', value: latestAudit.seo_score, icon: MagnifyingGlass, color: 'var(--warning)' },
                    { label: 'Best Practices', value: latestAudit.best_practices, icon: ChartLine, color: 'var(--accent)' },
                  ].map(m => {
                    const Icon = m.icon;
                    return (
                      <div key={m.label} className="nx-metric-card rounded-lg" data-testid={`audit-metric-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Icon size={18} style={{ color: m.color }} weight="duotone" />
                          <span className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider">{m.label}</span>
                        </div>
                        <p className={`text-3xl font-bold font-mono ${m.value >= 80 ? 'text-[var(--success)]' : m.value >= 60 ? 'text-[var(--warning)]' : 'text-[var(--error)]'}`}>
                          {m.value}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Tavily-Enriched Competitor Analysis */}
              {latestAudit?.competitor_analysis && latestAudit.competitor_analysis.source === 'tavily_live' && (
                <div className="nx-metric-card rounded-lg border-[var(--accent-border)]" data-testid="tavily-audit-insight">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightning size={16} className="text-[var(--accent)]" weight="fill" />
                    <span className="text-xs text-[var(--accent)] font-mono uppercase tracking-wider">Live Tavily Insight</span>
                    <span className="nx-tag nx-tag-green ml-auto">Live Data</span>
                  </div>
                  {latestAudit.competitor_analysis.answer && (
                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-3">{latestAudit.competitor_analysis.answer}</p>
                  )}
                  {latestAudit.competitor_analysis.sources?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {latestAudit.competitor_analysis.sources.map((s, j) => (
                        <a key={j} href={s.url} target="_blank" rel="noopener noreferrer"
                          className="text-[10px] text-[var(--text-tertiary)] font-mono hover:text-[var(--accent)] px-2 py-1 rounded bg-[var(--surface-sunken)] border border-[var(--border-light)] transition-colors duration-200">
                          {s.title?.substring(0, 40) || s.url}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="nx-metric-card rounded-lg" data-testid="audit-radar-chart">
                  <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Score Overview</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="var(--border)" />
                        <PolarAngleAxis dataKey="subject" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
                        <PolarRadiusAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }} />
                        <Radar dataKey="A" stroke="var(--accent)" fill="var(--accent)" fillOpacity={0.2} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="nx-metric-card rounded-lg" data-testid="audit-history">
                  <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Audit History</h3>
                  <table className="nx-table">
                    <thead>
                      <tr><th>Date</th><th>Type</th><th>Score</th><th>Issues</th><th>Fixed</th></tr>
                    </thead>
                    <tbody>
                      {audits.map(a => (
                        <tr key={a.id}>
                          <td className="font-mono text-xs">{new Date(a.created_at).toLocaleDateString()}</td>
                          <td><span className="nx-tag nx-tag-blue">{a.type}</span></td>
                          <td className={`font-mono ${a.score >= 80 ? 'text-[var(--success)]' : a.score >= 60 ? 'text-[var(--warning)]' : 'text-[var(--error)]'}`}>{a.score}</td>
                          <td className="font-mono">{a.issues_found}</td>
                          <td className="font-mono text-[var(--success)]">{a.issues_fixed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Keywords Tab */}
            <TabsContent value="keywords" className="space-y-4">
              <div className="nx-metric-card rounded-lg" data-testid="keywords-table">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Keyword Rankings</h3>
                <table className="nx-table">
                  <thead>
                    <tr><th>Keyword</th><th>Volume</th><th>Difficulty</th><th>Position</th><th>Change</th></tr>
                  </thead>
                  <tbody>
                    {keywords.map(k => (
                      <tr key={k.id}>
                        <td className="!text-[var(--text-primary)]">{k.keyword}</td>
                        <td className="font-mono">{k.volume.toLocaleString()}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-[var(--surface-sunken)] rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{
                                width: `${k.difficulty}%`,
                                background: k.difficulty >= 70 ? 'var(--error)' : k.difficulty >= 40 ? 'var(--warning)' : 'var(--success)'
                              }} />
                            </div>
                            <span className="font-mono text-xs">{k.difficulty}</span>
                          </div>
                        </td>
                        <td className="font-mono font-bold text-[var(--text-primary)]">#{k.position}</td>
                        <td>
                          <span className={`flex items-center gap-1 font-mono text-sm ${k.change > 0 ? 'text-[var(--success)]' : k.change < 0 ? 'text-[var(--error)]' : 'text-[var(--text-tertiary)]'}`}>
                            {k.change > 0 ? <ArrowUp size={12} /> : k.change < 0 ? <ArrowDown size={12} /> : null}
                            {k.change > 0 ? '+' : ''}{k.change}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="nx-metric-card rounded-lg" data-testid="keyword-volume-chart">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Search Volume Distribution</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={keywords.slice(0, 8)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis type="number" tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }} axisLine={false} />
                      <YAxis dataKey="keyword" type="category" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} width={150} axisLine={false} />
                      <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12 }} />
                      <Bar dataKey="volume" fill="var(--accent)" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </TabsContent>

            {/* Broken Links Tab */}
            <TabsContent value="broken-links" className="space-y-4">
              <div className="nx-metric-card rounded-lg" data-testid="broken-links-table">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                  <LinkBreak size={18} className="inline mr-2" weight="duotone" />
                  Broken Links ({brokenLinks.length})
                </h3>
                <table className="nx-table">
                  <thead>
                    <tr><th>URL</th><th>Status</th><th>Referring Page</th><th>Fixed</th></tr>
                  </thead>
                  <tbody>
                    {brokenLinks.map(l => (
                      <tr key={l.id}>
                        <td className="!text-[var(--text-primary)] font-mono text-xs max-w-xs truncate">{l.url}</td>
                        <td>
                          <span className={`nx-tag ${l.status_code === 404 ? 'nx-tag-red' : l.status_code === 301 ? 'nx-tag-yellow' : 'nx-tag-red'}`}>
                            {l.status_code}
                          </span>
                        </td>
                        <td className="font-mono text-xs text-[var(--text-secondary)] max-w-xs truncate">{l.referring_page}</td>
                        <td>
                          <span className={`nx-status-dot ${l.fixed ? 'active' : 'paused'}`} />
                          <span className="ml-2 text-xs">{l.fixed ? 'Fixed' : 'Pending'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* GEO Tab */}
            <TabsContent value="geo" className="space-y-4">
              {geoData && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: 'LLM Visibility', value: geoData.llm_visibility_score, suffix: '%' },
                      { label: 'ChatGPT Mentions', value: geoData.chatgpt_mentions },
                      { label: 'Perplexity Citations', value: geoData.perplexity_citations },
                      { label: 'JSON-LD Coverage', value: geoData.json_ld_coverage, suffix: '%' },
                    ].map(m => (
                      <div key={m.label} className="nx-metric-card rounded-lg" data-testid={`geo-metric-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
                        <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">{m.label}</p>
                        <p className="text-3xl font-bold text-[var(--text-primary)] font-mono">{m.value}{m.suffix || ''}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="nx-metric-card rounded-lg" data-testid="geo-trend-chart">
                      <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">GEO Score Trend</h3>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={geoData.trend_data}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                            <XAxis dataKey="month" tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }} axisLine={false} />
                            <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }} axisLine={false} />
                            <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12 }} />
                            <Line type="monotone" dataKey="score" stroke="var(--accent)" strokeWidth={2} dot={{ fill: 'var(--accent)', r: 4 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="nx-metric-card rounded-lg" data-testid="geo-recommendations">
                      <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Recommendations</h3>
                      <ul className="space-y-3">
                        {geoData.recommendations?.map((r, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-[var(--text-secondary)]">
                            <span className="w-5 h-5 rounded bg-[var(--accent-light)] text-[var(--accent)] text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-mono">
                              {i + 1}
                            </span>
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="nx-metric-card rounded-lg">
                    <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Answer-First Coverage</h3>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-3 bg-[var(--surface-sunken)] rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-[var(--accent)]" style={{ width: `${(geoData.answer_first_pages / geoData.total_pages) * 100}%` }} />
                      </div>
                      <span className="font-mono text-sm text-[var(--text-primary)]">
                        {geoData.answer_first_pages}/{geoData.total_pages} pages
                      </span>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Must-Win Prompt Tracker */}
            <TabsContent value="must-win" className="space-y-4">
              {mustWin && (
                <div className="nx-metric-card rounded-md">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-sm font-bold text-[var(--text-primary)]">
                      <Crosshair size={16} className="inline mr-2 text-[var(--accent)]" weight="duotone" />
                      Must-Win Prompt Tracker — {mustWin.cited_count}/{mustWin.total} cited
                    </h3>
                    <span className={`text-xs font-mono ${mustWin.gap > 10 ? 'text-[var(--error)]' : 'text-[var(--warning)]'}`}>
                      Gap: {mustWin.gap} uncited queries
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[10px] font-mono border-collapse must-win-table">
                      <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]">
                        <th className="text-left p-2">Query</th>
                        <th className="text-center p-2 llm-col hidden sm:table-cell">ChatGPT</th>
                        <th className="text-center p-2 llm-col hidden sm:table-cell">Perplexity</th>
                        <th className="text-center p-2 llm-col hidden sm:table-cell">Gemini</th>
                        <th className="text-center p-2 sm:hidden">Status</th>
                        <th className="text-center p-2 llm-col hidden sm:table-cell">7D Change</th>
                        <th className="text-center p-2 llm-col hidden sm:table-cell">Priority</th>
                        <th className="p-2">Action</th>
                      </tr></thead>
                      <tbody>
                        {mustWin.prompts?.map((p, i) => {
                          const anyCited = p.chatgpt?.cited || p.perplexity?.cited || p.gemini?.cited;
                          return (
                            <tr key={i} className={`border-b border-[var(--border-light)] ${!anyCited ? 'bg-[var(--error-bg)]' : ''}`}>
                              <td className="p-2 text-[var(--text-primary)] max-w-[200px]">{p.query}</td>
                              {['chatgpt', 'perplexity', 'gemini'].map(llm => (
                                <td key={llm} className="p-2 text-center llm-col hidden sm:table-cell">
                                  {p[llm]?.cited ? (
                                    <span className="text-[var(--success)]"><CheckCircle size={12} weight="fill" className="inline" /> #{p[llm].position || '?'}</span>
                                  ) : (
                                    <X size={12} className="text-[var(--error)] inline" weight="bold" />
                                  )}
                                </td>
                              ))}
                              <td className="p-2 text-center sm:hidden">
                                <span className={anyCited ? 'text-[var(--success)]' : 'text-[var(--error)]'}>{anyCited ? 'Cited' : 'Missing'}</span>
                              </td>
                              <td className="p-2 text-center llm-col hidden sm:table-cell">
                                <span className={`${p.change_7d === 'improved' ? 'text-[var(--success)]' : p.change_7d === 'declined' ? 'text-[var(--error)]' : 'text-[var(--text-tertiary)]'}`}>
                                  {p.change_7d === 'improved' ? <ArrowUp size={10} className="inline" /> : p.change_7d === 'declined' ? <ArrowDown size={10} className="inline" /> : '—'}
                                </span>
                              </td>
                              <td className="p-2 text-center llm-col hidden sm:table-cell">
                                <span className={`nx-tag text-[8px] ${p.priority === 'critical' ? 'nx-tag-red' : p.priority === 'high' ? 'nx-tag-yellow' : 'nx-tag-gray'}`}>{p.priority}</span>
                              </td>
                              <td className="p-2">
                                {!anyCited && (
                                  <Button size="sm" className="bg-[var(--accent)] text-white text-[8px] h-6 px-2" onClick={() => toast.success('GEO Agent triggered for this query')}>
                                    <Lightning size={8} className="mr-0.5" />Fix
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Answer-First 40-Word Rule */}
            <TabsContent value="answer-first" className="space-y-4">
              {answerFirst && (
                <div className="nx-metric-card rounded-md">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-sm font-bold text-[var(--text-primary)]">
                      Answer-First Coverage — {answerFirst.passing}/{answerFirst.total} pages pass
                    </h3>
                    <span className="text-xs font-mono text-[var(--text-tertiary)]">
                      Coverage: <b className="text-[var(--text-primary)]">{answerFirst.coverage_pct}%</b> — 3x more AI citations with Answer-First
                    </span>
                  </div>
                  <table className="w-full text-[10px] font-mono border-collapse">
                    <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]">
                      <th className="text-left p-2">URL</th>
                      <th className="text-left p-2">Opening 40 Words</th>
                      <th className="text-center p-2">40-Word Rule</th>
                      <th className="text-center p-2">Has Affiliate</th>
                      <th className="text-center p-2">Priority</th>
                    </tr></thead>
                    <tbody>
                      {answerFirst.pages?.map((p, i) => (
                        <tr key={i} className={`border-b border-[var(--border-light)] ${!p.passes_rule && p.has_affiliate_product ? 'bg-[var(--error-bg)]' : ''}`}>
                          <td className="p-2 text-[var(--accent)]">{p.url}</td>
                          <td className="p-2 text-[var(--text-secondary)] max-w-[300px] truncate">{p.opening_40_words}</td>
                          <td className="p-2 text-center">{p.passes_rule ? <CheckCircle size={12} className="text-[var(--success)] inline" weight="fill" /> : <X size={12} className="text-[var(--error)] inline" weight="bold" />}</td>
                          <td className="p-2 text-center">{p.has_affiliate_product ? <span className="nx-tag nx-tag-green text-[8px]">Affiliate</span> : '—'}</td>
                          <td className="p-2 text-center text-[var(--text-primary)]">{p.priority}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-[8px] text-[var(--text-tertiary)] font-mono mt-2 italic">Pages with Affiliate products + failed 40-word rule = highest priority GEO opportunity</p>
                </div>
              )}
            </TabsContent>

            {/* Vernacular Affiliate Content Gap */}
            <TabsContent value="vern-gap" className="space-y-4">
              {vernGap && (
                <div className="nx-metric-card rounded-md">
                  <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                    <Warning size={16} className="inline mr-2 text-[var(--warning)]" weight="fill" />
                    Vernacular Affiliate Content Gap — Products purchased but not cited in regional languages
                  </h3>
                  <table className="w-full text-[10px] font-mono border-collapse">
                    <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]">
                      <th className="text-left p-2">Product</th>
                      <th className="text-left p-2">SKU</th>
                      <th className="text-center p-2">Vernacular Purchases</th>
                      <th className="text-center p-2">Vernacular Citations</th>
                      <th className="text-center p-2">Gap Score</th>
                      <th className="text-left p-2">Language</th>
                      <th className="p-2">Action</th>
                    </tr></thead>
                    <tbody>
                      {vernGap.gaps?.map((g, i) => (
                        <tr key={i} className={`border-b border-[var(--border-light)] ${g.gap_score > 10 ? 'bg-[var(--warning-bg)]' : ''}`}>
                          <td className="p-2 text-[var(--text-primary)]">{g.product}</td>
                          <td className="p-2 text-[var(--accent)]">{g.sku}</td>
                          <td className="p-2 text-center text-[var(--success)]">{g.affiliate_purchases_vernacular}</td>
                          <td className="p-2 text-center text-[var(--error)]">{g.ai_citations_vernacular}</td>
                          <td className="p-2 text-center"><span className={`font-bold ${g.gap_score > 10 ? 'text-[var(--error)]' : g.gap_score > 5 ? 'text-[var(--warning)]' : 'text-[var(--success)]'}`}>{g.gap_score}x</span></td>
                          <td className="p-2"><span className="nx-tag nx-tag-blue text-[8px]">{g.primary_language}</span></td>
                          <td className="p-2">
                            <Button size="sm" className="bg-[var(--accent)] text-white text-[8px] h-6 px-2" onClick={() => toast.success(`Generating ${g.primary_language} content for ${g.product}`)}>
                              <Lightning size={8} className="mr-0.5" />Generate
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-[8px] text-[var(--warning)] font-mono mt-2">High gap score = products being purchased through regional language content but zero AI citations — highest-value GEO opportunity</p>
                </div>
              )}
            </TabsContent>

          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}
